import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserSelf } from '@tt/shared';
import { Alert } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { api, isApiError } from '@/lib/api';
import { keys } from '@/lib/queries';
import { AuthLayout } from './auth-layout';

export function LoginPage() {
  const { t } = useTranslation();
  const search = useSearch({ from: '/login' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const { user } = await api<{ user: UserSelf }>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      qc.setQueryData(keys.me, user);
      const target = search.redirect && search.redirect.startsWith('/') ? search.redirect : '/';
      await navigate({ to: target, replace: true });
    } catch (err) {
      if (isApiError(err)) {
        if (err.code === 'ACCOUNT_LOCKED') {
          const until = err.problem.lockedUntil as string | undefined;
          setError(t('auth.locked', { time: until ? new Date(until).toLocaleTimeString() : '' }));
        } else if (err.code === 'ACCOUNT_INACTIVE') setError(t('auth.accountInactive'));
        else if (err.status === 429) setError(t('auth.rateLimited'));
        // A dead or restarting API reaches us as Caddy's 502 with an empty body, which the client
        // turns into a synthetic HTTP_ERROR problem. Falling through to "invalid email or
        // password" told people their password was wrong when nothing had checked it — the most
        // expensive wrong error message in the application.
        else if (err.status >= 500) setError(t('errors.SERVER_UNAVAILABLE'));
        else setError(t('auth.invalidCredentials'));
      } else {
        setError(t('errors.NETWORK'));
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout title={t('auth.signIn')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error ? <Alert variant="error">{error}</Alert> : null}
        <Field label={t('auth.email')} required>
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label={t('auth.password')} required>
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" className="w-full" disabled={pending}>
          {t('auth.signIn')}
        </Button>
        <p className="text-center text-sm">
          <Link to="/forgot-password" className="text-primary underline-offset-4 hover:underline">
            {t('auth.forgotPassword')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
